import { useState } from "react";
import { Share2, MessageCircle, Copy, Check, X, Send } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Share the match URL (WhatsApp-friendly) so players open the app and set their availability.
 */
export default function ShareMatchButton({ match }) {
  const [copied, setCopied] = useState(false);
  const [openFallback, setOpenFallback] = useState(false);

  const isOff = match.match_type === "official";
  const url = `${window.location.origin}/matches/${match.id}`;
  const opponent = match.opponent_name ? ` vs ${match.opponent_name}` : "";
  const label = match.title || (isOff ? `Officiel${opponent}` : "Bleus vs Rouges");
  const message =
    `⚽ Match ASS les Régionnaires\n\n` +
    `📅 ${formatDate(match.match_date)}\n` +
    `📍 ${match.location || "Lieu à confirmer"}\n` +
    (isOff && match.opponent_name ? `🆚 ${match.opponent_name}\n` : "") +
    `\nMerci de donner ta disponibilité 👇\n${url}`;

  const share = async () => {
    const data = {
      title: `ASS les Régionnaires · ${label}`,
      text: message,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled
      }
    }
    // Fallback: show a small chooser popover
    setOpenFallback(true);
  };

  const openWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, "_blank", "noopener,noreferrer");
    setOpenFallback(false);
  };

  const openTelegram = () => {
    const tg = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`;
    window.open(tg, "_blank", "noopener,noreferrer");
    setOpenFallback(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Lien copié — colle-le dans ton groupe");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier — sélectionne manuellement");
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        data-testid="share-match-button"
        type="button"
        onClick={share}
        title="Partager la convocation aux joueurs"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold uppercase tracking-wide text-sm transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Partager
      </button>

      {openFallback && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setOpenFallback(false)}
          />
          <div
            data-testid="share-match-fallback"
            className="fixed inset-x-4 bottom-4 sm:absolute sm:inset-x-auto sm:bottom-auto sm:top-full sm:right-0 sm:mt-2 sm:w-72 z-50 rounded-2xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <div className="font-display font-bold text-sm uppercase">Partager la convocation</div>
              <button
                onClick={() => setOpenFallback(false)}
                className="p-1 rounded hover:bg-slate-800 text-slate-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-white/5">
              <button
                data-testid="share-match-whatsapp"
                onClick={openWhatsApp}
                className="w-full flex items-center gap-3 p-3 hover:bg-slate-800 text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-[#25D366]/20 text-[#25D366] flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-bold text-sm">WhatsApp</div>
                  <div className="text-xs text-slate-400">Ouvre l'app avec le message prêt</div>
                </div>
              </button>
              <button
                data-testid="share-match-telegram"
                onClick={openTelegram}
                className="w-full flex items-center gap-3 p-3 hover:bg-slate-800 text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-[#26A5E4]/20 text-[#26A5E4] flex items-center justify-center">
                  <Send className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-bold text-sm">Telegram</div>
                  <div className="text-xs text-slate-400">Envoyer dans un chat ou groupe</div>
                </div>
              </button>
              <button
                data-testid="share-match-copy"
                onClick={copyLink}
                className="w-full flex items-center gap-3 p-3 hover:bg-slate-800 text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-[#E5B85C]/20 text-[#E5B85C] flex items-center justify-center">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </div>
                <div>
                  <div className="font-bold text-sm">
                    {copied ? "Copié !" : "Copier le message"}
                  </div>
                  <div className="text-xs text-slate-400">Pour un SMS, email, …</div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
